/* Trading-mode badge.
 *
 * Specification sections 22 and 53: BACKTEST, PAPER and LIVE must never be
 * visually confusable. A trader who thinks they are in paper mode and is
 * actually live is the worst failure this UI can permit, so the mode is
 * always present, always in the same place, and colour is never the only cue —
 * the label spells it out.
 */
import type { TradingMode } from '@/types'

const CONFIG: Record<TradingMode, { label: string; color: string; title: string }> = {
  BACKTEST: {
    label: 'BACKTEST · SIMULATED',
    color: 'var(--mode-backtest)',
    title: 'Simulated execution on historical data. Not actual trading.',
  },
  PAPER: {
    label: 'PAPER · SIMULATED',
    color: 'var(--mode-paper)',
    title: 'Simulated execution on live market data. No real orders are sent.',
  },
  LIVE: {
    label: 'LIVE · REAL ORDERS',
    color: 'var(--mode-live)',
    title: 'Real orders are sent to a venue with real financial consequences.',
  },
}

export function ModeBadge({ mode }: { mode: TradingMode }) {
  const c = CONFIG[mode]
  return (
    <span
      title={c.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-1)',
        padding: '3px var(--sp-2)',
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${c.color}`,
        background: `color-mix(in srgb, ${c.color} 14%, transparent)`,
        color: c.color,
        fontSize: 'var(--fs-2xs)',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6, height: 6, borderRadius: '50%', background: c.color,
          boxShadow: mode === 'LIVE' ? `0 0 6px ${c.color}` : undefined,
        }}
      />
      {c.label}
    </span>
  )
}
